import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { api, apiConfig } from "../lib/apiClient";
import { syncFlagCacheUser } from "../hooks/useFeatureFlag";
import { syncExperienceModeUser } from "../hooks/useExperienceMode";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// 2f5c35b closeout — ONE API layer (apiClient) for every auth call, so the
// 401 / hard-block session-invalidation hook covers every screen.
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const me = await api.get("/api/auth/me");
      setUser(me);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Mode-UX closeout P0 — module caches (feature flags, experience mode) are
  // keyed to the signed-in user. Any account change (login, logout, switch)
  // wipes them so UI state can never leak across accounts.
  useEffect(() => {
    if (loading) return;
    const uid = user?.user_id || user?.email || null;
    syncFlagCacheUser(uid);
    syncExperienceModeUser(uid);
  }, [user, loading]);

  // Release-review — a 401 (dead session) or hard-block 403 (banned/deleted
  // mid-session) on ANY api call re-validates /me, which clears the user and
  // routes them to login instead of leaving a broken authenticated UI.
  const recheckGateRef = useRef(false);
  useEffect(() => {
    apiConfig.setAuthFailureHandler(() => {
      if (recheckGateRef.current) return;
      recheckGateRef.current = true;
      setTimeout(() => { recheckGateRef.current = false; }, 2000);
      checkAuth();
    });
    return () => apiConfig.setAuthFailureHandler(null);
  }, [checkAuth]);

  const login = async (email, password) => {
    const data = await api.post("/api/auth/login", { email, password });
    if (data && data.user_id && data.status !== "unverified") setUser(data);
    return data;
  };

  const signup = async (email, password, displayName, termsAccepted, profileChoice = "everyday_user", isInvited = false, acceptedVersions = null) => {
    const payload = {
      email, password, display_name: displayName, terms_accepted: termsAccepted,
      profile_choice: profileChoice, is_invited: isInvited
    };
    if (acceptedVersions && typeof acceptedVersions === 'object') {
      payload.accepted_legal = true;
      payload.accepted_versions = acceptedVersions;
    }
    return api.post("/api/auth/signup", payload);
  };

  const logout = async () => {
    try {
      await api.post("/api/auth/logout", {});
      setUser(null);
    } catch (error) {
      console.error("Logout failed:", error);
      setUser(null);
    }
    // Clear any bearer credential (native/RN strategy) so the next account
    // on this device can never inherit the previous session's token.
    try { apiConfig.setBearerToken(null); } catch { /* config reset is best-effort */ }
    // v4.3 Codex P0 — purge SW runtime caches so no private data survives
    // an account switch on this browser profile.
    try {
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'LOGOUT' });
      }
      if (window.caches && window.caches.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.filter((k) => k.endsWith('-runtime')).map((k) => caches.delete(k)));
      }
    } catch { /* cache purge is best-effort */ }
  };

  return <AuthContext.Provider value={{ user, setUser, loading, checkAuth, login, signup, logout, isAuthenticated: !!user }}>{children}</AuthContext.Provider>;
};
